// frontend/src/services/api.js
// Helpers del cliente (browser) para llamar a tu backend
import { authenticatedRequest, clearStoredToken, ensureFreshToken } from './authApi';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { tokenStore } from '../platform/tokenStore';

// Normalizamos la base y quitamos barras finales para evitar "doble //"
// Regla: en PROD preferimos same-origin (`/api/...`) para evitar CORS (especialmente en mobile/PWA).
// Sólo usamos localhost en dev local. Si REACT_APP_API_URL está seteado a localhost en prod, lo ignoramos.
const API = REST_API_ORIGIN;

// --- JWT helpers (shared)
function _isLikelyJwt(t) {
  const s = String(t || '').trim().replace(/^Bearer\s+/i, '').trim();

  // Must look like a real JWT: 3 base64url-ish segments separated by dots.
  // Also require typical JWT header prefix `eyJ` to avoid accepting JSON blobs.
  if (!s || !s.startsWith('eyJ')) return false;
  if (s.includes(' ')) return false;

  const parts = s.split('.');
  if (parts.length !== 3) return false;

  const b64urlRe = /^[A-Za-z0-9_-]+$/;
  if (!b64urlRe.test(parts[0]) || !b64urlRe.test(parts[1]) || !b64urlRe.test(parts[2])) return false;

  // Reasonable minimum length
  if (s.length < 60) return false;

  return true;
}

function _pickJwtFromObject(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const tok =
    obj.token ||
    obj.jwt ||
    obj.authToken ||
    obj.accessToken ||
    obj.idToken ||
    (obj.user && (obj.user.token || obj.user.jwt || obj.user.authToken || obj.user.accessToken)) ||
    (obj.session && (obj.session.token || obj.session.jwt || obj.session.authToken || obj.session.accessToken)) ||
    '';
  const cleaned = String(tok || '').trim().replace(/^Bearer\s+/i, '').trim();
  return _isLikelyJwt(cleaned) ? cleaned : '';
}

function _deepUnwrapJwt(raw, maxDepth = 3) {
  let cur = raw;
  for (let i = 0; i < maxDepth; i++) {
    const s = String(cur || '').trim();
    if (!s) return '';
    const cleaned = s.replace(/^Bearer\s+/i, '').trim();
    if (_isLikelyJwt(cleaned)) return cleaned;
    try {
      cur = JSON.parse(s);
      continue;
    } catch {
      return '';
    }
  }
  if (cur && typeof cur === 'object') return _pickJwtFromObject(cur);
  return '';
}

async function _persistJwtIfFound(data) {
  // Try common fields and also nested/double-encoded JSON
  let token = '';
  if (typeof data === 'string') token = _deepUnwrapJwt(data) || '';
  else token = _pickJwtFromObject(data) || _deepUnwrapJwt(JSON.stringify(data)) || '';

  if (token && _isLikelyJwt(token)) {
    await tokenStore.setAccessToken(token);
  }
  return token;
}

async function _setMemJwt(tok) {
  const cleaned = String(tok || '').trim().replace(/^Bearer\s+/i, '').trim();
  if (_isLikelyJwt(cleaned)) await tokenStore.setAccessToken(cleaned);
}
function _getMemJwt() {
  const authoritative = tokenStore.getAccessToken() || '';
  return _isLikelyJwt(authoritative) ? authoritative : '';
}

async function _persistJwtFromResponse(res, data) {
  if (!res || typeof res.headers?.get !== 'function') {
    return _persistJwtIfFound(data);
  }

  // Prefer explicit renewal headers
  const hdrCandidates = [
    res.headers.get('x-auth-token'),
    res.headers.get('x-auth-jwt'),
    res.headers.get('authorization'),
  ].filter(Boolean);

  for (const raw of hdrCandidates) {
    const cleaned = String(raw || '').trim().replace(/^Bearer\s+/i, '').trim();
    if (_isLikelyJwt(cleaned)) {
      await _setMemJwt(cleaned);
      return cleaned;
    }
  }

  // Fallback: if body contains token fields, persist them too
  return _persistJwtIfFound(data);
}

// --- Utilidad para armar headers con Bearer si hay token
function authHeaders() {
  if (typeof window === 'undefined') return {};

  // Guard: if a poisoned JSON/blob was stored as a token, wipe it (common bug: JSON.stringify(response)).
  const _purgeIfPoisoned = (s, k) => {
    try {
      const v = String(s.getItem(k) || '').trim();
      if (!v) return;
      const vv = v.replace(/^Bearer\s+/i, '').trim();
      // If it looks like JSON (or a non-JWT), purge it.
      if (vv.startsWith('{') || vv.startsWith('[')) {
        s.removeItem(k);
        return;
      }
      // If it is not a real JWT, also purge (prevents sending junk in Authorization)
      if (!_isLikelyJwt(vv)) {
        s.removeItem(k);
      }
    } catch {}
  };

  try {
    const keys = ['accessToken', 'token', 'authToken', 'jwt', 'idToken', 'zodiacus_token'];
    for (const k of keys) {
      _purgeIfPoisoned(localStorage, k);
      _purgeIfPoisoned(sessionStorage, k);
    }
  } catch {}

  const mem = _getMemJwt();
  if (mem) return { Authorization: `Bearer ${mem}` };

  const readStorage = (s, k) => {
    try {
      const v = s.getItem(k);
      return v == null ? '' : String(v);
    } catch {
      return '';
    }
  };

  // Short-circuit: check main token aliases first
  const directCandidates = [
    readStorage(localStorage, 'token'),
    readStorage(localStorage, 'authToken'),
    readStorage(localStorage, 'accessToken'),
    readStorage(sessionStorage, 'token'),
    readStorage(sessionStorage, 'authToken'),
    readStorage(sessionStorage, 'accessToken'),
  ];

  for (const raw of directCandidates) {
    const cleaned = String(raw || '').trim().replace(/^Bearer\s+/i, '').trim();
    if (_isLikelyJwt(cleaned)) {
      void _setMemJwt(cleaned).catch(() => false);
      return { Authorization: `Bearer ${cleaned}` };
    }
  }

  // Common places your app may store the JWT
  const candidates = [];

  // 1) Plain keys (prefer accessToken first)
  candidates.push(readStorage(localStorage, 'accessToken'));
  candidates.push(readStorage(localStorage, 'token'));
  candidates.push(readStorage(localStorage, 'authToken'));
  candidates.push(readStorage(localStorage, 'jwt'));
  candidates.push(readStorage(localStorage, 'idToken'));
  candidates.push(readStorage(localStorage, 'zodiacus_token'));
  candidates.push(readStorage(sessionStorage, 'accessToken'));
  candidates.push(readStorage(sessionStorage, 'token'));
  candidates.push(readStorage(sessionStorage, 'authToken'));
  candidates.push(readStorage(sessionStorage, 'jwt'));
  candidates.push(readStorage(localStorage, 'token_cookie'));
  candidates.push(readStorage(sessionStorage, 'token_cookie'));

  // 2) JSON blobs (including double-encoded JSON)
  candidates.push(_deepUnwrapJwt(readStorage(localStorage, 'userInfo')));
  candidates.push(_deepUnwrapJwt(readStorage(localStorage, 'user')));
  candidates.push(_deepUnwrapJwt(readStorage(localStorage, 'auth')));
  candidates.push(_deepUnwrapJwt(readStorage(localStorage, 'session')));
  candidates.push(_deepUnwrapJwt(readStorage(localStorage, 'zodiacus_user')));

  // 3) redux-persist style (persist:root) where nested JSON strings are stored
  try {
    const root = readStorage(localStorage, 'persist:root');
    if (root) {
      const parsedRoot = JSON.parse(root);
      const slices = ['auth', 'user', 'session', 'account'];
      for (const key of slices) {
        const sliceRaw = parsedRoot?.[key];
        if (typeof sliceRaw === 'string' && sliceRaw) {
          const tok = _deepUnwrapJwt(sliceRaw);
          if (tok) candidates.push(tok);
        } else if (sliceRaw && typeof sliceRaw === 'object') {
          const tok = _deepUnwrapJwt(JSON.stringify(sliceRaw));
          if (tok) candidates.push(tok);
        }
      }
    }
  } catch {}

  // Pick the first valid-looking token
  let token = '';
  for (const c of candidates) {
    const t = String(c || '').trim();
    if (!t) continue;
    const cleaned = t.replace(/^Bearer\s+/i, '').trim();
    if (_isLikelyJwt(cleaned)) {
      token = cleaned;
      break;
    }
  }

  // Final safety: never send a non-JWT in Authorization.
  if (token && !_isLikelyJwt(token)) {
    try { clearStoredToken(); } catch {}
    try {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('authToken');
    } catch {}
    token = '';
  }

  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- LOGIN (POST /api/auth/login)
// body: { fullName, password }
export async function apiLogin(body) {
  const res = await fetch(`${API}/api/auth/login`, {
    method: 'POST',
    credentials: 'include', // permite que viaje/reciba cookie si el server la setea
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(data.message || data.error || 'Login failed'), { status: res.status, data });
  }
  // Fallback: si el backend devuelve token en el body, guardarlo (3rd-party cookies pueden bloquearse)
  // Soporta variantes: token/jwt/authToken/accessToken y también objetos anidados.
  await _persistJwtFromResponse(res, data);
  return data;
}

// --- PROFILE (GET /api/auth/profile)
export async function apiGetProfile() {
  const res = await authenticatedRequest(`${API}/api/auth/profile`, {
    rawResponse: true,
    method: 'GET',
    credentials: 'include',
    headers: { ...authHeaders() },
  });

  const json = await res.json().catch(() => ({}));
  await _persistJwtFromResponse(res, json);

  if (!res.ok) {
    // Si el access token expiró, intentamos refresh y reintentamos profile 1 vez
    if (res.status === 401) {
      const refreshed = await _zcTryRefreshAuth();
      if (refreshed) {
        const res2 = await authenticatedRequest(`${API}/api/auth/profile`, {
          rawResponse: true,
          method: 'GET',
          credentials: 'include',
          headers: { ...authHeaders() },
        });
        const json2 = await res2.json().catch(() => ({}));
        await _persistJwtFromResponse(res2, json2);
        if (res2.ok) return json2;
      }
    }

    throw Object.assign(new Error(json.message || json.error || 'Unauthorized'), {
      status: res.status,
      data: json,
    });
  }

  return json;
}


// ===== ZODIAC CRUSH – PROFILE / LEADERBOARD (game economy) =====
// NOTE: These endpoints are already mounted under `/api/games/zodiac-crush/*` on the backend.
// We expose small helpers here so the UI can read the player economy (cristales, nivel, bestScore, etc)
// and the competitive tables without duplicating fetch boilerplate.

// ===== ZODIAC CRUSH – DAILY MISSIONS =====

// Cache en memoria (evita spam en dev/StrictMode)
let _zc_missions_inflight = null; // Promise en curso
let _zc_missions_last_ok = null;  // { ts:number, data:any }
let _zc_missions_rl_until = 0;    // timestamp ms: si hubo 429, evitamos spamear

// GET /api/games/zodiac-crush/profile
export async function apiGetZodiacCrushProfile(opts = {}) {
  const { force = false } = opts || {};

  // Small cache to avoid noisy mounts (StrictMode)
  // NOTE: profile changes less frequently than missions, so we can cache a bit longer.
  const now = Date.now();
  const TTL_MS = 3000;

  // Dedicated cache bucket (separate from missions)
  const bucket = _zc_profile_cache;

  if (!force && bucket.lastOk?.data && now - bucket.lastOk.ts < TTL_MS) {
    return bucket.lastOk.data;
  }

  if (!force && bucket.inflight) {
    return bucket.inflight;
  }

  const url = `${API}/api/games/zodiac-crush/profile`;

  bucket.inflight = (async () => {
    try {
      const data = await _zcFetchJson(url, { method: 'GET' });
      // If unauthorized, propagate a safe shape (UI can decide what to do)
      if (data && data.unauthorized) return data;
      bucket.lastOk = { ts: Date.now(), data };
      return data;
    } finally {
      bucket.inflight = null;
    }
  })();

  return bucket.inflight;
}

// GET /api/games/zodiac-crush/leaderboard?scope=global&limit=50
export async function apiGetZodiacCrushLeaderboard({ scope = 'global', limit = 50 } = {}) {
  const q = new URLSearchParams();
  if (scope) q.set('scope', String(scope));
  if (limit != null) q.set('limit', String(limit));

  const url = `${API}/api/games/zodiac-crush/leaderboard?${q.toString()}`;
  return _zcFetchJson(url, { method: 'GET' });
}

async function _zcTryRefreshAuth() {
  try {
    return Boolean(await ensureFreshToken());
  } catch {
    return false;
  }
}

async function _zcFetchJson(url, { method = 'GET', body = null } = {}) {
  const doFetch = async (mode = 'normal') => {
    // mode:
    //  - 'normal'  => headers include authHeaders()
    //  - 'cookie'  => NO Authorization header (cookie-only)
    if (process.env.NODE_ENV !== 'production') {
      const hasAuth = !!authHeaders().Authorization;
      if (!hasAuth) {
        // eslint-disable-next-line no-console
        console.warn('[ZodiacCrush] No Authorization header found for', url);
      }
    }

    const headers = {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(mode === 'cookie' ? {} : authHeaders()),
    };

    const res = await authenticatedRequest(url, {
      rawResponse: true,
      method,
      credentials: 'include',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json().catch(() => ({}));
    // If backend rotates/renews JWT via headers, capture it
    await _persistJwtFromResponse(res, data);
    return { res, data };
  };

  let { res, data } = await doFetch('normal');

  // 401 “después de un rato”: intentamos recuperar sesión/token UNA vez.
  // 1) Intento cookie-only (por si el server usa cookie httpOnly)
  // 2) Intento refresh via /api/auth/refresh (cookie refreshToken)
  // 3) Reintento normal
  if (res.status === 401) {
    // a) cookie-only probe (no Authorization) por si el server usa cookie httpOnly
    const probe = await doFetch('cookie');
    if (probe.res.ok) {
      ({ res, data } = await doFetch('normal'));
    } else {
      // b) refresh via /api/auth/refresh (cookie refreshToken)
      const refreshed = await _zcTryRefreshAuth();
      if (refreshed) {
        ({ res, data } = await doFetch('normal'));
      }
    }
  }

  // Rate-limit: degradar sin romper
  if (res.status === 429) {
    _zc_missions_rl_until = Date.now() + 4000;
    if (_zc_missions_last_ok?.data) return _zc_missions_last_ok.data;
    return { ok: false, rateLimited: true, missions: [] };
  }

  // ✅ Si sigue 401, NO tiramos error (evita spam y rompe menos el juego/UI)
  if (res.status === 401) {
    return { ok: false, unauthorized: true, status: 401 };
  }

  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      `HTTP ${res.status}`;
    throw Object.assign(new Error(msg), { status: res.status, data });
  }

  return data;
}

// GET /api/games/zodiac-crush/missions/today
export async function apiGetZodiacCrushMissionsToday(opts = {}) {
  const { force = false } = opts || {};

  // Cache de muy corto plazo para evitar múltiples mounts (StrictMode)
  const now = Date.now();
  const TTL_MS = 2500;

  // Si hace poco recibimos 429, no spameamos más requests
  if (!force && _zc_missions_rl_until && now < _zc_missions_rl_until) {
    if (_zc_missions_last_ok?.data) return _zc_missions_last_ok.data;
    return { ok: false, rateLimited: true, missions: [] };
  }

  if (!force && _zc_missions_last_ok?.data && now - _zc_missions_last_ok.ts < TTL_MS) {
    return _zc_missions_last_ok.data;
  }

  // Dedupe: si ya hay un request en vuelo, reusarlo
  if (!force && _zc_missions_inflight) {
    return _zc_missions_inflight;
  }

  const url = `${API}/api/games/zodiac-crush/missions/today`;

  _zc_missions_inflight = (async () => {
    try {
      const data = await _zcFetchJson(url, { method: 'GET' });
      if (data && data.unauthorized) return data;
      _zc_missions_last_ok = { ts: Date.now(), data };
      return data;
    } finally {
      _zc_missions_inflight = null;
    }
  })();

  return _zc_missions_inflight;
}

// POST /api/games/zodiac-crush/missions/progress
// POST /api/games/zodiac-crush/missions/progress
// Accepts either:
//  - delta object: apiReportZodiacCrushMissionProgress({ score: 123, ... })
//  - wrapped:      apiReportZodiacCrushMissionProgress({ delta: { score: 123, ... } })
export async function apiReportZodiacCrushMissionProgress(delta, { missionEventId } = {}) {
  const url = `${API}/api/games/zodiac-crush/missions/progress`;

  if (process.env.NODE_ENV !== 'production') {
    try { console.log("[ZC MISSIONS POST]", JSON.stringify(delta)); } catch {}
  }

  // Normalize so we ALWAYS send { delta: { ... } }
  const bodyObj = (() => {
    const v = delta && typeof delta === 'object' ? delta : {};
    // If caller already wrapped it as { delta: {...} }, unwrap one level
    if (v && typeof v.delta === 'object' && v.delta !== null && !Array.isArray(v.delta)) {
      return { delta: v.delta, ...(missionEventId ? { missionEventId } : {}) };
    }
    return { delta: v, ...(missionEventId ? { missionEventId } : {}) };
  })();

  const data = await _zcFetchJson(url, { method: 'POST', body: bodyObj });

  // Si no hay auth, no rompemos el juego: devolvemos estado simple.
  if (data && data.unauthorized) {
    return data;
  }

  // Si pudimos postear progreso, levantamos el cool-down (si había)
  _zc_missions_rl_until = 0;

  // ✅ IMPORTANT: bust missions cache so next GET /missions/today reflects latest progress
  // (si el POST no devuelve missions, esto evita que el UI se “resetee” al cambiar de nivel)
  _zc_missions_last_ok = null;

  // Si el backend devuelve el estado de misiones, lo cacheamos
  if (data && typeof data === 'object' && data.missions) {
    _zc_missions_last_ok = {
      ts: Date.now(),
      data: {
        ok: true,
        day: data.day,
        missions: data.missions,
        completed: data.completed,
      },
    };
  }

  return data;
}

// POST /api/games/zodiac-crush/run
// body: { finalScore, moves?, durationMs?, maxCascade? }
export function buildZodiacCrushRunRequest(body = {}, { runOperationId } = {}) {
  const payload = {
    finalScore: Number(body.finalScore ?? body.score ?? 0),
    moves: body.moves != null ? Number(body.moves) : undefined,
    durationMs: body.durationMs != null ? Number(body.durationMs) : undefined,
    maxCascade: body.maxCascade != null ? Number(body.maxCascade) : undefined,
    ...(body.replay && typeof body.replay === 'object'
      ? { replay: JSON.parse(JSON.stringify(body.replay)) }
      : {}),
    ...(runOperationId ? { runOperationId } : {}),
  };

  // Remove undefined fields
  Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
  return payload;
}

export async function apiSubmitZodiacCrushRun(body = {}, options = {}) {
  const payload = buildZodiacCrushRunRequest(body, options);
  const url = `${API}/api/games/zodiac-crush/run`;
  return _zcFetchJson(url, { method: 'POST', body: payload });
}

// --- UPDATE PROFILE (PUT /api/auth/profile)
// formData: FormData con { bio, gender?, photo? }
export async function apiUpdateProfile(formData) {
  const res = await authenticatedRequest(`${API}/api/auth/profile`, {
    rawResponse: true,
    method: 'PUT',
    credentials: 'include',
    headers: { ...authHeaders() },
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  await _persistJwtFromResponse(res, data);
  if (!res.ok) {
    throw Object.assign(new Error(data.message || data.error || 'Update failed'), { status: res.status, data });
  }
  return data;
}

export async function apiSendSupport({ subject, message, email, userId, displayName, appVersion }) {
  const res = await fetch(`${API}/api/support`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(), // Authorization: Bearer <token> si existe
    },
    body: JSON.stringify({
      subject,
      message,
      email,
      userId,        // 👈 ahora viajan al backend
      displayName,   // 👈
      appVersion: appVersion || 'N/D',
    }),
  });

  const json = await res.json().catch(() => ({}));
  await _persistJwtFromResponse(res, json);

  if (!res.ok || json.ok === false) {
    throw new Error(json.error || json.message || 'Error al enviar el mensaje al soporte');
  }

  return json;
}

// --- Utilidades de URL

// Construye URL absoluta (genérica) a recursos del backend
export function toBackendUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path; // ya es absoluta
  const clean = String(path).replace(/^\/+/, ''); // sin leading slash
  return `${API}/${encodeURI(clean)}`;
}

// Normaliza rutas de fotos servidas por el backend (profile_photos/*)
// - quita 'uploads/' si viene de la DB
// - garantiza prefijo 'profile_photos/'
// - devuelve absoluta y correctamente codificada
export function toAbsolutePhoto(p) {
  if (!p) return '/default-profile.png';
  if (/^https?:\/\//i.test(p)) return p;
  let clean = String(p)
    .replace(/^https?:\/\/[^/]+/i, '') // por si la guardaron con dominio
    .replace(/^\/+/, '')
    .replace(/^uploads\//, '');
  if (!clean.startsWith('profile_photos/')) clean = `profile_photos/${clean}`;
  return `${API}/${clean.split('/').map(encodeURIComponent).join('/')}`;
}
// eslint-disable-next-line import/no-anonymous-default-export
export default {
  apiLogin,
  apiGetProfile,
  apiUpdateProfile,
  apiGetZodiacCrushProfile,
  apiGetZodiacCrushLeaderboard,
  apiGetZodiacCrushMissionsToday,
  apiReportZodiacCrushMissionProgress,
  apiSubmitZodiacCrushRun,
  apiSendSupport,
  toBackendUrl,
  toAbsolutePhoto,
  getDailyTriviaQuestions,
};

// ===== Trivia (auto-rotating, deterministic daily) =====
// Lightweight client-side selector with remote JSON + local fallback
// Usage from HomeScreen: getDailyTriviaQuestions({ count: 8, userId })

function _mulberry32(a) {
  return function () {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function _hashString(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function _seededShuffle(arr, rand) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function _normalizeQuestions(raw) {
  const out = [];

  // Dedupe REAL por contenido (texto + respuestas), no por id random.
  const seen = new Set();

  const norm = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  // Hash estable (FNV-1a 32-bit) para generar ids determinísticos
  const fnv1a = (str) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(36);
  };

  (raw || []).forEach((q) => {
    if (!q || typeof q !== "object") return;

    const text = String(q.text || "").trim();
    if (!text || text.length < 8) return;

    const answersRaw = Array.isArray(q.answers) ? q.answers : [];
    const answersAll = answersRaw
      .filter((a) => a && typeof a.text === "string")
      .map((a) => ({
        text: String(a.text).trim(),
        correct: a.correct === true,
      }))
      .filter((a) => a.text.length > 0);

    // UI consistente: máximo 4 respuestas
    const answers = answersAll.slice(0, 4);

    // Necesitamos al menos 2 respuestas
    if (answers.length < 2) return;

    // Evitar respuestas duplicadas (mismo texto)
    const ansKeySet = new Set(answers.map((a) => norm(a.text)));
    if (ansKeySet.size !== answers.length) return;

    // Debe haber EXACTAMENTE 1 correcta
    const correctCount = answers.reduce((acc, a) => acc + (a.correct ? 1 : 0), 0);
    if (correctCount !== 1) return;

    // Firma estable por contenido: texto + set de respuestas (ignoramos orden)
    const answersSig = [...answers]
      .map((a) => `${norm(a.text)}:${a.correct ? "1" : "0"}`)
      .sort()
      .join("|");
    const contentSig = `${norm(text)}||${answersSig}`;

    if (seen.has(contentSig)) return;
    seen.add(contentSig);

    // ID determinístico
    const providedId = String(q.id || "").trim();
    const id = providedId || `q_${fnv1a(contentSig)}`;

    out.push({
      id,
      category: q.category || "general",
      difficulty: q.difficulty || "easy",
      text,
      answers: answers.map((a) => ({ text: a.text, correct: !!a.correct })),
      explanation: q.explanation || null,
      image: q.image || null,
    });
  });

  return out;
}
async function _fetchJsonWithTimeout(url, ms = 4000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}
function _todayKey() {
  // Siempre calcula el día en zona horaria Buenos Aires (consistente con el backend)
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const d = parts.find(p => p.type === 'day').value;
    return `${y}-${m}-${d}`;
  } catch {
    // Fallback sin Intl: ajustar manualmente a UTC-3 (AR no usa DST)
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const ba = new Date(utc - 3 * 60 * 60000);
    const y = ba.getFullYear();
    const m = String(ba.getMonth() + 1).padStart(2, '0');
    const d = String(ba.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}

// Preferimos un JSON grande (ej: 365 preguntas). Evitamos index.json para no generar 404.
const _TRIVIA_SOURCES = ['/assets/trivia/365.json'];

const _TRIVIA_CACHE_KEY = "zodiacus_trivia_cache_v1";

// Historial de preguntas usadas (por usuario) para evitar repetición entre días
const _TRIVIA_USED_Q_KEY_PREFIX = 'zodiacus_trivia_used_questions_v1:';

function _usedQKey(uid) {
  const u = (uid && String(uid)) ? String(uid) : 'anon';
  return _TRIVIA_USED_Q_KEY_PREFIX + u;
}

function _loadUsedQuestions(uid) {
  try {
    const raw = JSON.parse(localStorage.getItem(_usedQKey(uid)) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function _saveUsedQuestions(uid, arr) {
  try {
    localStorage.setItem(_usedQKey(uid), JSON.stringify(arr));
  } catch {}
}

function _diffDays(aKey, bKey) {
  // aKey/bKey: 'YYYY-MM-DD'
  try {
    const [ay, am, ad] = String(aKey).split('-').map(Number);
    const [by, bm, bd] = String(bKey).split('-').map(Number);
    const a = new Date(Date.UTC(ay, am - 1, ad));
    const b = new Date(Date.UTC(by, bm - 1, bd));
    return Math.floor((a - b) / (24 * 60 * 60 * 1000));
  } catch {
    return 0;
  }
}

function _pruneUsedQuestions(list, todayKey, { maxItems = 1200, maxDays = 180 } = {}) {
  const arr = Array.isArray(list) ? list.filter(Boolean) : [];

  // Normalizamos a { id, day } y filtramos basura
  const norm = arr
    .map((x) => {
      if (typeof x === 'string') return { id: x, day: todayKey };
      if (x && typeof x === 'object') {
        const id = String(x.id || '').trim();
        const day = String(x.day || '').trim();
        return id ? { id, day: /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : todayKey } : null;
      }
      return null;
    })
    .filter(Boolean);

  // Mantener sólo últimos maxDays (comparando contra todayKey)
  const recent = norm.filter((x) => {
    if (!x.day) return true;
    const age = Math.abs(_diffDays(todayKey, x.day));
    return age <= maxDays;
  });

  // Mantener orden de inserción (viejo -> nuevo) y capear
  if (recent.length <= maxItems) return recent;
  return recent.slice(recent.length - maxItems);
}
const _TRIVIA_LOCAL_FALLBACK = [
  {
    id: "aries_regente_1",
    category: "signos",
    difficulty: "easy",
    text: "¿Cuál es el planeta regente de Aries?",
    answers: [
      { text: "Marte", correct: true },
      { text: "Venus", correct: false },
      { text: "Mercurio", correct: false },
      { text: "Saturno", correct: false }
    ],
    explanation: "Aries está regido por Marte."
  },
  {
    id: "libra_elemento_1",
    category: "elementos",
    difficulty: "easy",
    text: "¿Cuál es el elemento de Libra?",
    answers: [
      { text: "Aire", correct: true },
      { text: "Agua", correct: false },
      { text: "Fuego", correct: false },
      { text: "Tierra", correct: false }
    ]
  },
  {
    id: "escorpio_rasgo_1",
    category: "signos",
    difficulty: "easy",
    text: "Un rasgo típico de Escorpio es…",
    answers: [
      { text: "Intensidad", correct: true },
      { text: "Indiferencia", correct: false },
      { text: "Desapego extremo", correct: false },
      { text: "Superficialidad", correct: false }
    ]
  }
];

export async function getDailyTriviaQuestions({
  count = 8,
  categories = [],
  difficulties = [],
  userId = null,
} = {}) {
  const day = _todayKey();
  const safeUid = (userId && String(userId)) ? String(userId) : 'anon';

  // Cache por usuario (evita mezclar trivias entre cuentas)
  const CACHE_KEY = `${_TRIVIA_CACHE_KEY}:${safeUid}`;
  const USED_CATS_KEY = `zodiacus_trivia_used_categories_v1:${safeUid}`;

  // 1) Intentar devolver cache del día (misma trivia si recarga)
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    if (raw && raw.day === day && Array.isArray(raw.data) && raw.data.length) {
      return raw.data.slice(0, Math.max(1, count));
    }
  } catch {}

  // 2) Construir pool unificado desde las fuentes
  let pool = [];
  for (const url of _TRIVIA_SOURCES) {
    try {
      const data = await _fetchJsonWithTimeout(url, 3500);
      pool = pool.concat(_normalizeQuestions(data));
    } catch {
      // sigue
    }
  }
  if (pool.length === 0) pool = _normalizeQuestions(_TRIVIA_LOCAL_FALLBACK);

  // 3) Filtros explícitos (si se pasan manualmente)
  if (categories?.length) {
    const set = new Set(categories.map(String));
    pool = pool.filter((q) => set.has(String(q.category)));
  }
  if (difficulties?.length) {
    const set = new Set(difficulties.map(String));
    pool = pool.filter((q) => set.has(String(q.difficulty)));
  }

  const N = Math.max(1, Number(count) || 1);

  // 4) RNG determinístico por (día + usuario)
  const seedStr = `${day}:${safeUid}`;
  const rand = _mulberry32(_hashString(seedStr));

  // 5) Historial de preguntas usadas (por usuario): evita repetición entre días
  let usedQ = _loadUsedQuestions(safeUid);
  usedQ = _pruneUsedQuestions(usedQ, day, { maxItems: 500, maxDays: 180 });
  const usedQSet = new Set(usedQ.map((x) => String(x?.id || '').trim()).filter(Boolean));

  // Pool preferido: preguntas NO usadas
  const poolUnused = pool.filter((q) => !usedQSet.has(String(q?.id || '').trim()));

  // 6) Rotación de categorías (por usuario) sobre el pool disponible
  const baseForCategories = poolUnused.length ? poolUnused : pool;
  const allCategories = Array.from(
    new Set(baseForCategories.map((q) => String(q?.category || 'general')))
  );

  // Cargar categorías usadas (por usuario)
  let usedCategories = [];
  try {
    const rawUsed = JSON.parse(localStorage.getItem(USED_CATS_KEY) || '[]');
    if (Array.isArray(rawUsed)) usedCategories = rawUsed.map(String);
  } catch {
    usedCategories = [];
  }

  // Limpiar categorías que ya no existen (por cambios de data)
  usedCategories = usedCategories.filter((c) => allCategories.includes(String(c)));

  let availableCategories = allCategories.filter((c) => !usedCategories.includes(c));
  if (!availableCategories.length) {
    availableCategories = allCategories.slice();
    usedCategories = [];
  }

  let todayCategory = null;
  if (availableCategories.length) {
    const idx = Math.floor(rand() * availableCategories.length);
    todayCategory = availableCategories[idx];
  }

  // 7) workingPool: primero intentamos dentro de la categoría elegida y SIN usadas
  const pickFrom = (src) => {
    if (!todayCategory) return src;
    const filtered = src.filter((q) => String(q?.category || 'general') === String(todayCategory));
    return filtered.length ? filtered : src;
  };

  let workingPool = pickFrom(poolUnused.length ? poolUnused : pool);
  if (!workingPool.length) workingPool = pool;

  // 8) Elegir preguntas: shuffle determinístico + garantía de no duplicadas
  const uniquePicked = [];
  const seenIds = new Set();

  const pushUnique = (q) => {
    const id = String(q?.id || '').trim();
    if (!id) return;
    if (seenIds.has(id)) return;
    seenIds.add(id);
    uniquePicked.push(q);
  };

  for (const q of _seededShuffle(workingPool, rand)) {
    pushUnique(q);
    if (uniquePicked.length >= N) break;
  }

  // 9) Fallback: si faltan (pool chico por historial), completamos con pool total
  if (uniquePicked.length < N) {
    for (const q of _seededShuffle(pool, rand)) {
      pushUnique(q);
      if (uniquePicked.length >= N) break;
    }
  }

  // 10) Persistir cache del día + categoría usada + ids usados
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ day, data: uniquePicked, category: todayCategory })
    );

    if (todayCategory) {
      const nextUsed = Array.from(new Set([...usedCategories, todayCategory]));
      localStorage.setItem(USED_CATS_KEY, JSON.stringify(nextUsed));
    }

    // Guardar IDs usados (con day) para evitar repetición futura
    const appended = usedQ.concat(uniquePicked.map((q) => ({ id: String(q?.id || '').trim(), day })));
const pruned = _pruneUsedQuestions(appended, day, { maxItems: 500, maxDays: 180 });
_saveUsedQuestions(safeUid, pruned);
  } catch {
    // ignoramos errores de storage
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[TRIVIA] day:', day, 'uid:', safeUid, 'cat:', todayCategory, 'pool:', pool.length, 'unused:', poolUnused.length, 'picked:', uniquePicked.length);
  }

  return uniquePicked;
}
// Canonical token keys and module-scoped profile cache
let _zc_profile_cache = { inflight: null, lastOk: null };
